import { APICode } from "@/constants";
import type {
  ActionOneParam,
  FotorResult,
  TCollectOption,
  TEffectClassify,
  TServerDeTectFaceData,
  TTaskOptions,
  TTaskV2SigleOptions,
} from "@/types";
import { getRandomId } from "@/utils";
import axios from "axios";
import { uniqueId } from "lodash-es";

const axiosInstance = axios.create({
  timeout: 60000,
  withCredentials: true,
  baseURL: process.env.REACT_APP_BASE_URL,
});

axiosInstance.interceptors.request.use(
  (config: any) => {
    config.headers["x-app-id"] = "app-fotor-web";
    config.headers["Content-Type"] = config.headers["Content-Type"]
      ? config.headers["Content-Type"]
      : "application/x-www-form-urlencoded";
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const getCredits = () => {
  return axiosInstance.get("/api/create/getScore", {}).then((res) => res.data);
};

export const getSubscriptions = () => {
  return axiosInstance
    .get("/api/app/payment/subscriptions", {})
    .then((res) => res.data);
};

export const getLoginState = () => {
  return axiosInstance.get("/User/GetLoginState", {}).then((res) => res.data);
};

export const getPresetGroupList = (type = "single") => {
  return axiosInstance
    .get("/api/image/faceswap/group", {
      params: {
        type,
      },
    })
    .then((res) => res.data);
};

export const getSignUrl = (extension: string = "jpg") => {
  return axiosInstance
    .get(
      "/api/create/sign",

      {
        params: {
          mimeType: extension,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
    .then((res) => res.data);
};

export const putFileTo = (
  file: File,
  url: string,
  onProgress?: (value: any) => void
) => {
  return axios({
    method: "PUT",
    url,
    data: file,
    headers: {
      "Content-Type": file.type,
    },
    onUploadProgress: (e) => {
      const total = e.total || 0;
      const current = e.loaded || 0;
      if (total > 0) {
        const percentCompleted = Math.round((current * 100) / total);
        onProgress?.(percentCompleted);
      }
    },
    withCredentials: false,
  }).then((res: any) => {
    if (res?.status === 200) {
      return { code: APICode.Success };
    } else {
      throw new Error();
    }
  });
};

export const postTask = (data: TTaskOptions) => {
  return axiosInstance
    .post("/api/create/faceswaps/multi", data, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => res.data);
};

export const getTask = (taskId: string) => {
  const url = `/api/create/get_task?taskId=${taskId}`;
  return axiosInstance.get(url, {}).then((res) => res.data);
};

export const postCollect = (data: TCollectOption) => {
  return axiosInstance
    .post("/api/create/collection_tasks", data, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    })
    .then((res) => res.data);
};

/**获取label列表 */

export const getLabelList = (language = "en_US") => {
  return axiosInstance
    .get("/api/create/action-figure-filter/category", {
      headers: { language: language },
    })
    .then((res) => res.data as FotorResult<TEffectClassify[]>);
};

export const getPointsActivityPublicKey = (language = "en_US") => {
  return axiosInstance
    .get("/api/v1/credits/activity/public-key", {
      headers: { language: language },
    })
    .then((res) => res.data);
};

export const pointsActivityCheckIn = (sign = "", language = "en_US") => {
  const params = new URLSearchParams();
  params.append("sign", sign);
  return axiosInstance
    .post("/api/v1/credits/activity/sync-complete", params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        language: language,
      },
    })
    .then((res) => res.data);
};

/**
 * 人脸检测
 */
export const faceDetect = (sourceImage: string) => {
  return axiosInstance
    .post(
      "/api/faces/detect",
      {
        source_image: sourceImage,
        task: "detect",
        request_id: getRandomId(),
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
    .then((res) => res.data as { faces: TServerDeTectFaceData[] });
};

/**
 * 单人换脸
 * @param data
 * @returns
 */
export const singleFaceSwap = (data: TTaskV2SigleOptions) => {
  return axiosInstance
    .post("/api/create/faceswaps", data, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => res.data);
};

/**
 * 多人换脸
 * @param data
 * @returns
 */
export const mutiFaceSwap = (data: TTaskOptions) => {
  return axiosInstance
    .post("/api/create/faceswaps/multi", data, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => res.data);
};

export const getRemainNums = (businessType: string) => {
  return axiosInstance
    .get(`/api/create/${businessType}/remainNums`, {
      params: {
        method: "free",
      },
    })
    .then((res) => res.data);
};

export const deleteAigcTask = (taskId: string) => {
  const formData = new FormData();
  formData.append("taskIds", taskId);
  return axiosInstance
    .delete("api/create/generate_picture/tasks", {
      data: formData,
    })
    .then((res) => res.data);
};
/**
 * 下载文件，返回进度
 * @param callback
 */
export const downloadFileWithUrl = (
  url: string,
  callback?: ActionOneParam<number>
) => {
  return axios({
    method: "get",
    url: url,
    responseType: "blob",
    // 下载超时时间设置长一些
    timeout: 60 * 1000 * 10,
    onDownloadProgress: (progressEvent) => {
      // console.log('===progressEvent:',progressEvent)
      const total = progressEvent.total || 0;
      const current = progressEvent.loaded || 0;
      callback && callback(total > 0 ? Math.round((current * 100) / total) : 0);
    },
  });
};
